import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useToggle } from 'react-use';
import SimpleModal from 'components/SimpleModal';
import DeactivationReasonSelect from 'components/DeactivationReasonSelect';
import { selectTeamHash } from 'selectors';
import { deactivateTeams as _deactivateTeams } from 'thunk';
import { OnSuccessV2 } from 'types';
import { Box, TextField, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DeactivationReason } from '@constants/deactivation';
import noop from 'lodash/noop';

const modalPropsDefault = {
  teamIds: [],
  onSuccess: noop,
  skipStateUpdate: false,
};

type ModalPropsType = {
  teamIds: number[];
  onSuccess?: OnSuccessV2;
  skipStateUpdate?: boolean;
};

const useDeactivateTeams = () => {
  const [isModalOpen, toggleModal] = useToggle(false);
  const [{ teamIds, onSuccess, skipStateUpdate }, setModalProps] =
    useState<ModalPropsType>(modalPropsDefault);
  const dispatch = useAppDispatch();
  const teamHash = useAppSelector(selectTeamHash);
  const [dataRetentionDays, setDataRetentionDays] = useState<number | undefined>();
  const [deactivationReason, setDeactivationReason] = useState<DeactivationReason | ''>('');
  const isConfirmDisabled =
    !deactivationReason ||
    (dataRetentionDays !== undefined &&
      (!Number.isInteger(dataRetentionDays) || dataRetentionDays <= 0));

  const onClose = useCallback(() => {
    toggleModal(false);
    setDataRetentionDays(undefined);
    setDeactivationReason('');
  }, [toggleModal]);

  const deactivateTeams = useCallback(
    ({
      teamIds,
      onSuccess,
      skipStateUpdate,
      dataRetentionDays,
      deactivationReason,
      teamTargets,
    }: {
      teamIds?: number[];
      onSuccess?: OnSuccessV2;
      skipStateUpdate?: boolean;
      dataRetentionDays?: number;
      deactivationReason: DeactivationReason;
      teamTargets?: Array<{
        teamId: number;
        tenant?: string;
        realmId?: string;
      }>;
    }) => {
      const targets: Array<{
        teamId: number;
        tenant?: string;
        realmId?: string;
      }> =
        teamTargets && teamTargets.length
          ? teamTargets
          : (teamIds || []).map((id) => ({ teamId: id }));

      dispatch(
        _deactivateTeams({
          teamTargets: targets,
          onSuccess,
          skipStateUpdate,
          dataRetentionDays,
          deactivationReason,
        }),
      );
    },
    [dispatch],
  );

  const onConfirm = useCallback(() => {
    if (!deactivationReason) {
      return;
    }
    deactivateTeams({
      teamIds,
      onSuccess,
      skipStateUpdate,
      dataRetentionDays,
      deactivationReason,
    });
    onClose();
  }, [
    deactivateTeams,
    teamIds,
    onSuccess,
    onClose,
    skipStateUpdate,
    dataRetentionDays,
    deactivationReason,
  ]);

  const handleOpenDeactivateTeamsModal = useCallback(
    (nextModalProps: ModalPropsType) => {
      setModalProps(nextModalProps);
      toggleModal(true);
    },
    [toggleModal],
  );

  const ModalComponent = isModalOpen && (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={onConfirm}
      headerText="Teams Deactivation"
      confirmButtonText="Deactivate"
      onConfirmButtonClassNames="red-warning-button"
      disableConfirmButton={isConfirmDisabled}
      Body={
        <div>
          <div>
            You are about to deactivate <b>{teamIds.length}</b>{' '}
            {teamIds.length > 1 ? 'Teams' : 'Team'}.
          </div>
          <Box component="ul" maxHeight={350} overflow="auto">
            {teamIds.map((id) => {
              const team = teamHash[id];
              const label = team?.name || String(id);
              return <li key={id}>{label}</li>;
            })}
          </Box>
          <div>
            This action will revoke access to the accounts for all members effective immediately.
          </div>
          <Box mt={2}>
            <DeactivationReasonSelect value={deactivationReason} onChange={setDeactivationReason} />
          </Box>
          <Box mt={2}>
            <TextField
              label={
                <Box display="flex" alignItems="center">
                  <Box mr={0.5}>Custom Data Retention Period (days)</Box>
                  <Tooltip
                    title={
                      'The time between "team gets deactivated" and "team\'s data gets hard-deleted"'
                    }
                  >
                    <InfoIcon style={{ fontSize: 16, color: '#6b7280' }} />
                  </Tooltip>
                </Box>
              }
              type="number"
              inputProps={{
                min: '1',
                step: '1',
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataRetentionDays ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setDataRetentionDays(value === '' ? undefined : Number(value));
              }}
              placeholder="Optional. Will use 180 days as default if not provided"
            />
          </Box>
        </div>
      }
    />
  );

  return {
    DeactivateTeamsConfirmModal: ModalComponent,
    handleOpenDeactivateTeamsModal,
    deactivateTeams,
  };
};

export default useDeactivateTeams;
